import { useState, useEffect, useCallback, useRef } from "react";
import { IProduct } from "../types";
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  clearBuild,
} from "../services/storage";

interface UseBuildPersistenceOptions {
  creatorSlug: string;
  onRehydrated?: (components: Record<string, IProduct>) => void;
  onRehydrationError?: (error: Error, missingProducts: string[]) => void;
}

interface UseBuildPersistenceReturn {
  isRehydrating: boolean;
  rehydrationError: Error | null;
  saveBuild: (components: Record<string, IProduct>) => void;
  clearStoredBuild: () => void;
}

export function useBuildPersistence({
  creatorSlug,
  onRehydrated,
  onRehydrationError,
}: UseBuildPersistenceOptions): UseBuildPersistenceReturn {
  const [isRehydrating, setIsRehydrating] = useState(true);
  const [rehydrationError, setRehydrationError] = useState<Error | null>(null);
  const hasRehydrated = useRef(false);

  // Rehydrate build on mount
  useEffect(() => {
    if (hasRehydrated.current) return;
    hasRehydrated.current = true;

    try {
      const storedComponents = loadFromLocalStorage(creatorSlug);

      if (
        storedComponents &&
        Object.keys(storedComponents).length > 0 &&
        onRehydrated
      ) {
        onRehydrated(storedComponents);
      }
    } catch (error) {
      console.error("[PERSISTENCE] Rehydration failed:", error);
      setRehydrationError(
        error instanceof Error ? error : new Error("Unknown error")
      );
      if (onRehydrationError) {
        onRehydrationError(
          error instanceof Error ? error : new Error("Unknown error"),
          []
        );
      }
    } finally {
      setIsRehydrating(false);
    }
  }, [creatorSlug, onRehydrated, onRehydrationError]);

  // Save build to localStorage
  const saveBuild = useCallback(
    (components: Record<string, IProduct>) => {
      saveToLocalStorage(components, creatorSlug);
    },
    [creatorSlug]
  );

  // Clear stored build
  const clearStoredBuild = useCallback(() => {
    clearBuild(creatorSlug);
  }, [creatorSlug]);

  return {
    isRehydrating,
    rehydrationError,
    saveBuild,
    clearStoredBuild,
  };
}
